import {
  ArchiveIcon,
  BookMinusIcon,
  BookPlusIcon,
  DownloadIcon,
  ExternalLinkIcon,
  HistoryIcon,
  LinkIcon,
  MessageSquarePlusIcon,
  MoreVerticalIcon,
  PencilIcon,
  ReplaceIcon,
  RotateCcwIcon,
  Share2Icon,
  SquarePenIcon,
  Trash2Icon,
} from 'lucide-react';
import type { JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';

export interface ArtifactKnowledgeAction {
  mode: 'add' | 'remove';
  onSelect: () => void;
}

/** A link out of the app to the file in the place it lives, such as SharePoint or OneDrive. */
export interface ArtifactExternalLink {
  /** The menu item's text, such as "Open in SharePoint". */
  label: string;
  /** Already checked to be plain http(s) by the caller. */
  href: string;
}

export interface ArtifactActionsMenuProps {
  artifactName: string;
  onEditContent?: () => void;
  onUploadNewVersion?: () => void;
  onVersionHistory?: () => void;
  onRename?: () => void;
  onAddToChat?: () => void;
  onCopyLink?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  openExternally?: ArtifactExternalLink;
  knowledgeAction?: ArtifactKnowledgeAction;
  onDelete?: () => void;
  onArchive?: () => void;
  onRestoreFile?: () => void;
}

/**
 * The canonical artifact action menu shared by list rows, tiles and viewers. The component only
 * presents callbacks: the surface that renders it owns permissions, dialogs and mutations.
 */
export function ArtifactActionsMenu({
  artifactName,
  onEditContent,
  onUploadNewVersion,
  onVersionHistory,
  onRename,
  onAddToChat,
  onCopyLink,
  onDownload,
  onShare,
  openExternally,
  knowledgeAction,
  onArchive,
  onRestoreFile,
  onDelete,
}: ArtifactActionsMenuProps): JSX.Element {
  const hasFileActions =
    onEditContent !== undefined ||
    onUploadNewVersion !== undefined ||
    onVersionHistory !== undefined ||
    onRename !== undefined;
  const hasSharingActions =
    onAddToChat !== undefined ||
    onCopyLink !== undefined ||
    onDownload !== undefined ||
    onShare !== undefined ||
    openExternally !== undefined ||
    knowledgeAction !== undefined;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-current"
          aria-label={`Actions for ${artifactName}`}
          onClick={(event) => event.stopPropagation()}
        >
          <MoreVerticalIcon className="size-4 text-current" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-52">
        {onEditContent ? (
          <DropdownMenuItem
            aria-label={`Edit content of ${artifactName}`}
            onClick={onEditContent}
          >
            <SquarePenIcon className="size-4" /> Edit content
          </DropdownMenuItem>
        ) : null}
        {onUploadNewVersion ? (
          <DropdownMenuItem
            aria-label={`Upload a new version of ${artifactName}`}
            onClick={onUploadNewVersion}
          >
            <ReplaceIcon className="size-4" /> Upload new version
          </DropdownMenuItem>
        ) : null}
        {onVersionHistory ? (
          <DropdownMenuItem
            aria-label={`Version history for ${artifactName}`}
            onClick={onVersionHistory}
          >
            <HistoryIcon className="size-4" /> Version history
          </DropdownMenuItem>
        ) : null}
        {onRename ? (
          <DropdownMenuItem
            aria-label={`Rename ${artifactName}`}
            onClick={onRename}
          >
            <PencilIcon className="size-4" /> Rename
          </DropdownMenuItem>
        ) : null}
        {hasFileActions && hasSharingActions ? <DropdownMenuSeparator /> : null}
        {onAddToChat ? (
          <DropdownMenuItem
            aria-label={`Add ${artifactName} to chat`}
            onClick={onAddToChat}
          >
            <MessageSquarePlusIcon className="size-4" /> Add to chat
          </DropdownMenuItem>
        ) : null}
        {onCopyLink ? (
          <DropdownMenuItem
            aria-label={`Copy link to ${artifactName}`}
            onClick={onCopyLink}
          >
            <LinkIcon className="size-4" /> Copy link
          </DropdownMenuItem>
        ) : null}
        {onDownload ? (
          <DropdownMenuItem
            aria-label={`Download ${artifactName}`}
            onClick={onDownload}
          >
            <DownloadIcon className="size-4" /> Download
          </DropdownMenuItem>
        ) : null}
        {onShare ? (
          <DropdownMenuItem
            aria-label={`Share ${artifactName}`}
            onClick={onShare}
          >
            <Share2Icon className="size-4" /> Share…
          </DropdownMenuItem>
        ) : null}
        {openExternally ? (
          <DropdownMenuItem asChild>
            <a
              href={openExternally.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${openExternally.label}: ${artifactName}`}
            >
              <ExternalLinkIcon className="size-4" /> {openExternally.label}
            </a>
          </DropdownMenuItem>
        ) : null}
        {knowledgeAction ? (
          <DropdownMenuItem
            aria-label={`${knowledgeAction.mode === 'remove' ? 'Remove' : 'Add'} ${artifactName} ${knowledgeAction.mode === 'remove' ? 'from' : 'to'} the knowledge base`}
            onClick={knowledgeAction.onSelect}
          >
            {knowledgeAction.mode === 'remove' ? (
              <BookMinusIcon className="size-4" />
            ) : (
              <BookPlusIcon className="size-4" />
            )}
            {knowledgeAction.mode === 'remove'
              ? 'Remove from knowledge base'
              : 'Add to knowledge base'}
          </DropdownMenuItem>
        ) : null}
        {onArchive ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              aria-label={`Archive ${artifactName}`}
              onClick={onArchive}
            >
              <ArchiveIcon className="size-4" /> Archive
            </DropdownMenuItem>
          </>
        ) : null}
        {onRestoreFile ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              aria-label={`Restore ${artifactName}`}
              onClick={onRestoreFile}
            >
              <RotateCcwIcon className="size-4" /> Restore file
            </DropdownMenuItem>
          </>
        ) : null}
        {onDelete ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              aria-label={`Delete ${artifactName}`}
              onClick={onDelete}
            >
              <Trash2Icon className="size-4" /> Delete file
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
